import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { makeEmptyFilters } from "./utils/constants"
import { useColumnSettings } from "./hooks/useColumnSettings"
import BulkActionBar from "./components/BulkActionBar"
import { FiltersContent } from "./components/Filters"
import RequestsTable from "./components/RequestsTable"
import ColumnSettingsModal from "./components/ColumnSettingsModal"
import { motion, AnimatePresence } from "framer-motion"
import ConfirmModal from "./components/ConfirmModal"
import MobileRequestCard from "./components/MobileRequestCard"
import MobileFiltersDrawer from "./components/Filters/MobileFiltersDrawer"
import { createPortal } from "react-dom"
import {
  useBulkDeleteBundlesMutation,
  useBulkUpdateBundleStatusMutation,
  useGetBundlesQuery,
  buildBundleExportUrl,
} from "../../../app/services/book/bundle"
import { buildFilterParams } from "./utils/buildFilterParams"
import { useFilterOptions } from "./hooks/useFilterOptions"

const cleanColor = (val) => (val ? String(val).trim() : "")

const DEFAULT_PER_PAGE_OPTIONS = [15, 25, 50, 100]

// "display" — обычный показ на экране (текущее поведение onApply),
// всё остальное — экспорт через GET /book/bundle/export.
const EXPORT_TYPE_VIEWS = ["pdf", "xls", "xls_old", "xls_financial", "xls_financial_franchise"]

const QUICK_FILTER_ICONS = {
  "fa-retweet": (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
      />
    </svg>
  ),
  "fa-fire": (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z"
      />
    </svg>
  ),
  "fa-percent": (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M8 8a2 2 0 11-4 0 2 2 0 014 0zm12 8a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  ),
  "fa-unlock": (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 11V7a4 4 0 118 0M5 11h14a1 1 0 011 1v8a1 1 0 01-1 1H5a1 1 0 01-1-1v-8a1 1 0 011-1z"
      />
    </svg>
  ),
}

const App = ({ isAdmin }) => {
  const { t } = useTranslation()
  const ef = () => makeEmptyFilters()

  const {
    columns,
    colOrder,
    visibleCols,
    applyColumnSettings,
    isLoading: colsLoading,
    templates,
    departments,
    appliedTemplateId,
    applyTemplate,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  } = useColumnSettings()

  const { OPT, quickFilters } = useFilterOptions()

  const [filtersOpen, setFiltersOpen] = useState(false)
  const [mobileFilters, setMobileFilters] = useState(false)
  const [selectedRows, setSelectedRows] = useState([])
  const [pf, setPFState] = useState(ef())
  const [applied, setApplied] = useState(ef())
  const [activeStatus, setActiveStatus] = useState(null)
  const [quickFilter, setQuickFilter] = useState(null)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(15)
  const [showBulkMenu, setShowBulkMenu] = useState(false)
  const [showColModal, setShowColModal] = useState(false)
  const [openSelectId, setOpenSelectId] = useState(null)
  const [confirmModal, setConfirmModal] = useState(null)
  const [confirmError, setConfirmError] = useState(null)
  const [bulkMenuPos, setBulkMenuPos] = useState({ top: 0, left: 0 })
  const [hoveredStatusId, setHoveredStatusId] = useState(null)
  const [tabTooltip, setTabTooltip] = useState(null) // { top, left, text }

  const bulkRef = useRef(null)
  const bulkBtnRef = useRef(null)
  const bulkDropRef = useRef(null)

  const {
    data: bundlesData,
    isFetching,
    refetch,
  } = useGetBundlesQuery(
    {
      columns: visibleCols,
      page,
      perPage,
      filters: {
        ...buildFilterParams(applied, activeStatus),
        ...(quickFilter?.filter ?? {}),
      },
    },
    { skip: colsLoading }
  )

  const [bulkUpdateStatus, { isLoading: isBulkStatusUpdating }] =
    useBulkUpdateBundleStatusMutation()
  const [bulkDeleteBundles, { isLoading: isBulkDeleting }] = useBulkDeleteBundlesMutation()

  const isConfirming = isBulkStatusUpdating || isBulkDeleting

  const pageData = bundlesData?.data ?? []
  const totalPages = bundlesData?.total_pages ?? 1
  const total = bundlesData?.total ?? 0
  const permissions = bundlesData?.permissions ?? {}

  const statusTabs = bundlesData?.status_tabs ?? []

  useEffect(() => {
    if (bundlesData?.per && bundlesData.per !== perPage) {
      setPerPage(bundlesData.per)
    }
  }, [bundlesData?.per])

  const perPageOptions = bundlesData?.per_page_options ?? DEFAULT_PER_PAGE_OPTIONS

  useEffect(() => {
    if (!showBulkMenu) return
    const h = (e) => {
      if (bulkBtnRef.current?.contains(e.target)) return
      if (bulkDropRef.current?.contains(e.target)) return
      setShowBulkMenu(false)
    }
    document.addEventListener("mousedown", h)
    return () => document.removeEventListener("mousedown", h)
  }, [showBulkMenu])

  const setPF = (k, v) => setPFState((f) => ({ ...f, [k]: v }))
  const setPFCond = (k, v) => setPFState((f) => ({ ...f, condition: { ...f.condition, [k]: v } }))

  const hasActive = !!(
    applied.status.length ||
    applied.search ||
    applied.dateRequest.from ||
    applied.dateCheckin.from ||
    applied.dateCheckout.from
  )

  // GET /book/bundle/export?type_view=...&filter[...]=... — открываем в
  // новой вкладке текущим набором фильтров (то, что стоит в форме прямо
  // сейчас, а не то, что уже "применено" к таблице).
  const triggerExport = (typeView) => {
    const filters = {
      ...buildFilterParams(pf, activeStatus),
      ...(quickFilter?.filter ?? {}),
    }
    const url = buildBundleExportUrl(typeView, filters)
    window.open(url, "_blank", "noopener")
  }

  const applyFilters = () => {
    if (EXPORT_TYPE_VIEWS.includes(pf.displayResults)) {
      triggerExport(pf.displayResults)
      // возвращаем селект в "На экране", чтобы повторный клик на Filter
      // не переоткрывал экспорт молча
      setPFState((f) => ({ ...f, displayResults: "display" }))
      return
    }

    setApplied({
      ...pf,
      condition: { ...pf.condition },
      dateRequest: { ...pf.dateRequest },
      dateCheckin: { ...pf.dateCheckin },
      dateCheckout: { ...pf.dateCheckout },
    })
    setActiveStatus(null)
    setQuickFilter(null)
    setPage(1)
  }

  const clearFilters = () => {
    const e = ef()
    setPFState(e)
    setApplied(e)
    setActiveStatus(null)
    setQuickFilter(null)
    setPage(1)
  }

  const toggleQuickFilter = (qf) => {
    const isActive = quickFilter?.key === qf.key
    setQuickFilter(isActive ? null : { key: qf.key, filter: qf.filter })
    setApplied(ef())
    setActiveStatus(null)
    setPage(1)
  }

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage)
    setPage(1)
  }

  const allSelected = pageData.length > 0 && pageData.every((r) => selectedRows.includes(r.id))

  const toggleAll = () => {
    if (allSelected)
      setSelectedRows((p) => p.filter((id) => !pageData.map((r) => r.id).includes(id)))
    else setSelectedRows((p) => [...new Set([...p, ...pageData.map((r) => r.id)])])
  }

  const toggleRow = (id) =>
    setSelectedRows((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))

  const ordCols = colOrder.filter((id) => visibleCols.includes(id))

  const filterProps = {
    pf,
    setPF,
    setPFCond,
    openSelectId,
    setOpenSelectId,
    onApply: applyFilters,
    onClear: clearFilters,
    OPT,
    permissions,
  }

  const tableLoading = colsLoading || isFetching

  const closeConfirmModal = () => {
    if (isConfirming) return
    setConfirmModal(null)
    setConfirmError(null)
  }

  const runBulkDelete = async (ids) => {
    try {
      setConfirmError(null)
      await bulkDeleteBundles({ bundleIds: ids }).unwrap()
      setSelectedRows((prev) => prev.filter((id) => !ids.includes(id)))
      setConfirmModal(null)
      refetch()
    } catch (e) {
      setConfirmError(t("admin.bulk_action_error"))
    }
  }

  const runBulkStatus = async (ids, statusId) => {
    try {
      setConfirmError(null)
      await bulkUpdateStatus({ bundleIds: ids, statusId }).unwrap()
      setSelectedRows((prev) => prev.filter((id) => !ids.includes(id)))
      setConfirmModal(null)
      refetch()
    } catch (e) {
      setConfirmError(t("admin.bulk_action_error"))
    }
  }

  const handleQuickFilterHover = (e, label) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const TOOLTIP_WIDTH = 224
    const MARGIN = 8
    const anchorX = rect.left + rect.width / 2

    const clampedX = Math.min(
      Math.max(anchorX, TOOLTIP_WIDTH / 2 + MARGIN),
      window.innerWidth - TOOLTIP_WIDTH / 2 - MARGIN
    )

    setTabTooltip({
      top: rect.top,
      left: clampedX,
      arrowOffset: anchorX - clampedX,
      text: label,
    })
  }

  return (
    <div className="min-h-screen bg-slate-100 font-sans">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-md">
        <div className="px-3 sm:px-5 pt-3 pb-3 flex items-center justify-end gap-2 border-b border-gray-100">
          <button
            onClick={() => setMobileFilters(true)}
            className={`sm:hidden flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-medium transition-all
              ${hasActive ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-white border-gray-200 text-gray-600"}`}
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            {t("admin.filters")}
            {hasActive && <span className="w-2 h-2 rounded-full bg-blue-500" />}
          </button>

          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className={`hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-medium transition-all
              ${filtersOpen ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            {t("admin.filters")}
            {hasActive && <span className="w-2 h-2 rounded-full bg-blue-500 ml-0.5" />}
            <motion.svg
              animate={{ rotate: filtersOpen ? 180 : 0 }}
              transition={{ duration: 0.22 }}
              className="w-3 h-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </motion.svg>
          </button>

          {/* Columns button */}
          <button
            onClick={() => setShowColModal(true)}
            className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-medium text-gray-600 hover:bg-gray-50 transition-all"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
              />
            </svg>
            <span className="hidden sm:inline">{t("filter.columns")}</span>
          </button>

          {isAdmin && (
            <>
              <button
                onClick={() => window.open("/book/bundle_request/bulk_update_prices", "_blank")}
                className="hidden sm:flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-medium text-gray-600 hover:bg-gray-50 transition-all"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                  />
                </svg>
                {t("admin.mass_update_transport_prices")}
              </button>

              <button
                onClick={() => window.open("/book/import/orders", "_blank")}
                className="hidden sm:flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-medium text-gray-600 hover:bg-gray-50 transition-all"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                  />
                </svg>
                {t("admin.import_requests")}
              </button>

              <button
                onClick={() => window.open("/book/import/payments", "_blank")}
                className="hidden sm:flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-medium text-gray-600 hover:bg-gray-50 transition-all"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                  />
                </svg>
                {t("admin.import_payments")}
              </button>
            </>
          )}
        </div>

        <AnimatePresence initial={false}>
          {selectedRows.length > 0 && (
            <BulkActionBar
              count={selectedRows.length}
              bulkRef={bulkRef}
              bulkBtnRef={bulkBtnRef}
              showBulkMenu={showBulkMenu}
              setShowBulkMenu={setShowBulkMenu}
              onDeleteClick={() =>
                setConfirmModal({
                  type: "delete_bulk",
                  data: { count: selectedRows.length },
                  onConfirm: () => runBulkDelete(selectedRows),
                })
              }
              onDeselectAll={() => setSelectedRows([])}
              setBulkMenuPos={setBulkMenuPos}
            />
          )}
        </AnimatePresence>

        <AnimatePresence initial={false}>
          {filtersOpen && (
            <motion.div
              key="fp"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              style={{ overflow: "hidden" }}
              className="hidden sm:block border-b border-gray-100"
            >
              <FiltersContent {...filterProps} />
            </motion.div>
          )}
        </AnimatePresence>

        <div className="px-3 sm:px-5 py-2.5 flex items-center gap-1.5 sm:gap-2 overflow-x-auto scrollbar-hide">
          {statusTabs.map((s) => {
            const isActive = String(activeStatus) === String(s.id)
            const bg = cleanColor(s.background)
            const fg = cleanColor(s.color)
            return (
              <motion.button
                key={s.id}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onMouseEnter={(e) => {
                  if (!s.description) return
                  const rect = e.currentTarget.getBoundingClientRect()
                  const TOOLTIP_WIDTH = 224
                  const MARGIN = 8
                  const anchorX = rect.left + rect.width / 2

                  const clampedX = Math.min(
                    Math.max(anchorX, TOOLTIP_WIDTH / 2 + MARGIN),
                    window.innerWidth - TOOLTIP_WIDTH / 2 - MARGIN
                  )

                  setTabTooltip({
                    top: rect.top,
                    left: clampedX,
                    arrowOffset: anchorX - clampedX,
                    text: s.description,
                  })
                }}
                onMouseLeave={() => setTabTooltip(null)}
                onClick={() => {
                  setActiveStatus(isActive ? null : s.id)
                  setApplied(ef())
                  setQuickFilter(null)
                  setPage(1)
                }}
                style={{
                  backgroundColor: bg || "#ffffff",
                  color: fg || "#374151",
                  borderColor: isActive ? fg || "#374151" : "#e5e7eb",
                }}
                className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-xl border text-xs font-medium transition-all whitespace-nowrap flex-shrink-0
          ${isActive ? "ring-2 ring-offset-1" : "hover:opacity-80"}`}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: fg || "#9ca3af" }}
                />
                <span>{s.label}</span>
                <span className="opacity-60 font-normal">{s.count ?? ""}</span>
              </motion.button>
            )
          })}

          {quickFilters.length > 0 && (
            <>
              <span className="w-px h-5 bg-gray-200 flex-shrink-0 mx-0.5" />
              {quickFilters.map((qf) => {
                const isActive = quickFilter?.key === qf.key
                return (
                  <motion.button
                    key={qf.key}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    type="button"
                    onMouseEnter={(e) => handleQuickFilterHover(e, qf.label)}
                    onMouseLeave={() => setTabTooltip(null)}
                    onClick={() => toggleQuickFilter(qf)}
                    className={`flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-xl border text-xs font-medium transition-all whitespace-nowrap flex-shrink-0
                      ${
                        isActive
                          ? "bg-blue-50 border-blue-300 text-blue-700 ring-2 ring-offset-1 ring-blue-200"
                          : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                  >
                    {QUICK_FILTER_ICONS[qf.icon] ?? null}
                    <span className="hidden sm:inline">{qf.label}</span>
                    <span className="opacity-60 font-normal">({qf.count})</span>
                  </motion.button>
                )
              })}
            </>
          )}
        </div>
      </div>

      <div className="px-3 sm:px-5 py-4 sm:py-5">
        {/* Mobile: cards */}
        <div className="sm:hidden space-y-3">
          {tableLoading ? (
            <div className="h-40 flex items-center justify-center text-gray-400 text-sm">
              {t("admin.loading")}
            </div>
          ) : pageData.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center text-gray-400 shadow-sm border border-gray-200">
              <svg
                className="w-10 h-10 mx-auto mb-3 text-gray-200"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="text-sm">{t("filter.no_records")}</p>
            </div>
          ) : (
            pageData.map((row) => (
              <MobileRequestCard
                key={row.id}
                row={row}
                statusTabs={statusTabs}
                selected={selectedRows.includes(row.id)}
                onToggle={() => toggleRow(row.id)}
                onEdit={() => console.log("edit", row.id)}
                onDelete={() =>
                  setConfirmModal({
                    type: "delete_one",
                    data: { rowId: row.id },
                    onConfirm: () => runBulkDelete([row.id]),
                  })
                }
              />
            ))
          )}

          {totalPages > 1 && !tableLoading && (
            <div className="flex flex-col gap-2 pt-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  {total === 0 ? 0 : (page - 1) * perPage + 1}–{Math.min(page * perPage, total)}{" "}
                  {t("filter.of")} {total}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-xl border border-gray-200 bg-white text-gray-600 disabled:opacity-30 transition-all"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                  <span className="px-3 py-2 text-sm font-medium text-gray-700">
                    {page} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 rounded-xl border border-gray-200 bg-white text-gray-600 disabled:opacity-30 transition-all"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-end">
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                  {perPageOptions.map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => n !== perPage && handlePerPageChange(n)}
                      className={`relative px-2.5 py-1 rounded-md text-xs font-semibold transition-colors
              ${n === perPage ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                    >
                      {n === perPage && (
                        <motion.span
                          layoutId="perPageActivePillMobile"
                          transition={{ duration: 0.18, ease: "easeOut" }}
                          className="absolute inset-0 bg-white rounded-md shadow-sm"
                        />
                      )}
                      <span className="relative">{n}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="hidden sm:block">
          {tableLoading ? (
            <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
              {t("admin.loading")}
            </div>
          ) : (
            <RequestsTable
              data={pageData}
              columns={columns}
              ordCols={ordCols}
              statusTabs={statusTabs}
              selectedRows={selectedRows}
              allSelected={allSelected}
              onToggleAll={toggleAll}
              onToggleRow={toggleRow}
              onDelete={(id) =>
                setConfirmModal({
                  type: "delete_one",
                  data: { rowId: id },
                  onConfirm: () => runBulkDelete([id]),
                })
              }
              onEdit={(id) => console.log("edit", id)}
              page={page}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              perPage={perPage}
              perPageOptions={perPageOptions}
              onPerPageChange={handlePerPageChange}
            />
          )}
        </div>
      </div>

      <MobileFiltersDrawer
        open={mobileFilters}
        onClose={() => setMobileFilters(false)}
        {...filterProps}
      />

      <AnimatePresence>
        {showColModal && (
          <ColumnSettingsModal
            columns={columns}
            order={colOrder}
            visible={visibleCols}
            onApply={applyColumnSettings}
            onClose={() => setShowColModal(false)}
            isAdmin={isAdmin}
            templates={templates}
            departments={departments}
            appliedTemplateId={appliedTemplateId}
            onApplyTemplate={applyTemplate}
            onCreateTemplate={createTemplate}
            onUpdateTemplate={updateTemplate}
            onDeleteTemplate={deleteTemplate}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showBulkMenu && (
          <motion.div
            ref={bulkDropRef}
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
            style={{
              position: "fixed",
              top: bulkMenuPos.top,
              left: bulkMenuPos.left,
              zIndex: 9999,
              minWidth: 240,
            }}
            className="bg-white border border-gray-200 rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50/60">
              <p className="text-xs font-semibold text-gray-700">
                {t("filter.new_status_for", { count: selectedRows.length })}
              </p>
            </div>
            <div className="p-1.5 max-h-72 overflow-y-auto">
              {statusTabs.map((s) => {
                const fg = cleanColor(s.color)
                const bg = cleanColor(s.background)
                return (
                  <button
                    key={s.id}
                    onClick={() => {
                      setShowBulkMenu(false)
                      setConfirmModal({
                        type: "status_bulk",
                        data: { count: selectedRows.length, status: s.id },
                        onConfirm: () => runBulkStatus(selectedRows, s.id),
                      })
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-gray-50 transition-colors text-left"
                  >
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0 border"
                      style={{ backgroundColor: bg || "#e5e7eb", borderColor: fg || "#d1d5db" }}
                    />
                    <span className="text-xs text-gray-700">{s.label}</span>
                  </button>
                )
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {confirmModal && (
          <ConfirmModal
            type={confirmModal.type}
            data={confirmModal.data}
            onConfirm={confirmModal.onConfirm}
            onClose={closeConfirmModal}
            statusTabs={statusTabs}
            isConfirming={isConfirming}
            errorMessage={confirmError}
          />
        )}
      </AnimatePresence>
      {tabTooltip &&
        createPortal(
          <AnimatePresence>
            <div
              style={{
                position: "fixed",
                top: tabTooltip.top,
                left: tabTooltip.left,
                transform: "translate(-50%, calc(-100% - 8px))",
                zIndex: 9999,
              }}
              className="pointer-events-none w-56"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.12 }}
              >
                <div className="bg-white border border-gray-200 rounded-xl shadow-lg px-3 py-2 text-xs text-gray-600 leading-snug whitespace-pre-line">
                  {tabTooltip.text}
                </div>
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: `calc(50% + ${tabTooltip.arrowOffset}px)`,
                    transform: "translate(-50%, -4px) rotate(45deg)",
                  }}
                  className="w-2.5 h-2.5 bg-white border-b border-r border-gray-200"
                />
              </motion.div>
            </div>
          </AnimatePresence>,
          document.body
        )}
    </div>
  )
}

export default App
